import React, { useState, useEffect } from 'react';
import { Session, Feature, Component, PRFeedback, PRFeedbackCommentType, PRFeedbackSeverity, PRFeedbackTypeItem } from '../../types';
import { AppMessage } from '../../utils/messages';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../utils/api';

interface PRFeedbackModalProps {
  session: Session;
  allFeatures: Feature[];
  allComponents: Component[];
  isOpen: boolean;
  onClose: () => void;
  onFeedbackAdded: (feedback: PRFeedback, isEdit?: boolean) => Promise<void> | void;
  showMessage: (msg: AppMessage) => void;
  prefillData?: {
    featureId?: string;
    componentId?: string;
  };
  editingFeedback?: PRFeedback | null;
}

const PRFeedbackModal: React.FC<PRFeedbackModalProps> = ({
  session,
  allFeatures,
  allComponents,
  isOpen,
  onClose,
  onFeedbackAdded,
  showMessage,
  prefillData,
  editingFeedback
}) => {
  const { user } = useAuth();
  const [prUrl, setPrUrl] = useState('');
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>(prefillData?.featureId || '');
  const [selectedComponentId, setSelectedComponentId] = useState<string>(prefillData?.componentId || '');
  const [commentUrl, setCommentUrl] = useState('');
  const [commentType, setCommentType] = useState<PRFeedbackCommentType>('');
  const [severity, setSeverity] = useState<PRFeedbackSeverity>('');
  const [description, setDescription] = useState('');
  
  // Configurable types from database
  const [commentTypes, setCommentTypes] = useState<PRFeedbackTypeItem[]>([]);
  const [severities, setSeverities] = useState<PRFeedbackTypeItem[]>([]);
  
  // Fetch configurable types based on session's productId
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const [commentTypesData, severitiesData] = await Promise.all([
          apiRequest<PRFeedbackTypeItem[]>('/api/prfeedback-comment-types', {}, { useAuth: true }),
          apiRequest<PRFeedbackTypeItem[]>('/api/prfeedback-severities', {}, { useAuth: true })
        ]);
        
        // Filter by productId if session has one
        const productId = session.productId;
        const filterByProduct = (items: PRFeedbackTypeItem[]) => {
          if (!productId) return items;
          return items.filter(item => 
            !item.productIds || 
            item.productIds.length === 0 || 
            item.productIds.includes(productId)
          );
        };
        
        const filteredCommentTypes = filterByProduct(commentTypesData || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        const filteredSeverities = filterByProduct(severitiesData || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        
        setCommentTypes(filteredCommentTypes);
        setSeverities(filteredSeverities);
        
        // Set default values if not editing
        if (!editingFeedback && filteredCommentTypes.length > 0 && !commentType) {
          setCommentType(filteredCommentTypes[0].name);
        }
        if (!editingFeedback && filteredSeverities.length > 0 && !severity) {
          setSeverity(filteredSeverities[0].name);
        }
      } catch (error) {
        console.error('Error fetching PR feedback types:', error);
      }
    };
    
    if (isOpen) {
      fetchTypes();
    }
  }, [isOpen, session.productId, editingFeedback, commentType, severity]);

  // Initialize PR URL from reference URL if available, or from editing feedback
  useEffect(() => {
    if (isOpen) {
      if (editingFeedback) {
        // Editing mode - prefill with existing feedback data
        setPrUrl(editingFeedback.prUrl);
        setCommentUrl(editingFeedback.commentUrl || '');
        setSelectedFeatureId(editingFeedback.featureId);
        setSelectedComponentId(editingFeedback.componentId);
        setCommentType(editingFeedback.commentType);
        setSeverity(editingFeedback.severity);
        setDescription(editingFeedback.description || '');
      } else {
        // New feedback mode
        // Use reference URL as PR URL if it exists
        if (session.referenceUrl && session.referenceUrl.trim()) {
          setPrUrl(session.referenceUrl.trim());
        } else {
          setPrUrl('');
        }
        setSelectedFeatureId(prefillData?.featureId || '');
        setSelectedComponentId(prefillData?.componentId || '');
        setCommentUrl('');
        // Defaults will be set by the fetchTypes effect
        setCommentType('');
        setSeverity('');
        setDescription('');
      }
    }
  }, [isOpen, session.referenceUrl, prefillData, editingFeedback]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleAddFeedback = async () => {
    if (!prUrl.trim()) {
      showMessage({ type: 'error', text: 'Please enter a PR URL.' });
      return;
    }

    if (!commentUrl.trim()) {
      showMessage({ type: 'error', text: 'Please enter a Comment URL.' });
      return;
    }

    if (!selectedFeatureId) {
      showMessage({ type: 'error', text: 'Please select a feature for this feedback.' });
      return;
    }

    if (!selectedComponentId) {
      showMessage({ type: 'error', text: 'Please select a component for this feedback.' });
      return;
    }

    // Get impact from the selected comment type
    const selectedCommentTypeItem = commentTypes.find(ct => ct.name === commentType);
    let impact = editingFeedback?.impact || '';
    
    // If creating new feedback and comment type has an associated impact, fetch the impact name
    if (!editingFeedback && selectedCommentTypeItem?.impactId) {
      try {
        const impactsData = await apiRequest<PRFeedbackTypeItem[]>('/api/prfeedback-impacts', {}, { useAuth: true });
        const productId = session.productId;
        const filteredImpacts = productId
          ? (impactsData || []).filter(imp => 
              !imp.productIds || 
              imp.productIds.length === 0 || 
              imp.productIds.includes(productId)
            )
          : (impactsData || []);
        const associatedImpact = filteredImpacts.find(imp => imp.id === selectedCommentTypeItem.impactId);
        if (associatedImpact) {
          impact = associatedImpact.name;
        }
      } catch (error) {
        console.error('Error fetching impact for comment type:', error);
      }
    }

    // Create or update PRFeedback object
    const isEdit = !!editingFeedback;
    const newFeedback: PRFeedback = {
      id: editingFeedback?.id || `pr-feedback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      prUrl: prUrl.trim(),
      commentUrl: commentUrl.trim(),
      commentType: commentType,
      severity: severity,
      impact: impact,
      description: description.trim() || undefined,
      componentId: selectedComponentId,
      featureId: selectedFeatureId,
      createdAt: editingFeedback?.createdAt || new Date().toISOString(),
      createdBy: editingFeedback?.createdBy || user?.id || ''
    };

    // Reset form and close modal immediately (optimistic UI update)
    setPrUrl(session.referenceUrl?.trim() || '');
    setSelectedFeatureId(prefillData?.featureId || '');
    setSelectedComponentId(prefillData?.componentId || '');
    setCommentUrl('');
    setCommentType('');
    setSeverity('');
    setDescription('');
    onClose();
    
    // Call the callback to handle adding or updating the feedback
    const result = onFeedbackAdded(newFeedback, isEdit);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        console.error(`Error ${isEdit ? 'updating' : 'adding'} PR feedback:`, error);
        if (error instanceof Error) {
          showMessage({ type: 'error', text: error.message || `Failed to ${isEdit ? 'update' : 'add'} PR feedback. Please try again.` });
        } else {
          showMessage({ type: 'error', text: `Failed to ${isEdit ? 'update' : 'add'} PR feedback. Please try again.` });
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay" 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        overflow: 'hidden',
        zIndex: 1000,
        pointerEvents: 'auto'
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.preventDefault()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="card modal-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="title-lg mb-md">{editingFeedback ? 'Edit PR Feedback' : 'PR Feedback'}</h2>

        {/* PR URL Input */}
        <div className="mb-md">
          <label className="label">PR URL <span className="text-danger">*</span></label>
          <input
            type="url"
            placeholder="https://github.com/org/repo/pull/123"
            value={prUrl}
            onChange={(e) => setPrUrl(e.target.value)}
            className="form-control"
            onKeyPress={(e) => e.key === 'Enter' && handleAddFeedback()}
          />
          {session.referenceUrl && (
            <div className="bug-url-hint-runner" style={{ fontSize: '0.875rem', color: '#666', fontStyle: 'italic', marginTop: '0.25rem' }}>
              Pre-filled from session reference URL
            </div>
          )}
        </div>

        {/* Feature selector (required) */}
        <div className="mb-sm">
          <label className="label">Feature <span className="text-danger">*</span></label>
          <select
            value={selectedFeatureId}
            onChange={(e) => {
              setSelectedFeatureId(e.target.value);
              setSelectedComponentId(''); // Reset component when feature changes
            }}
            className="form-control"
            required
          >
            <option value="">Select a feature...</option>
            {allFeatures.map(feature => (
              <option key={feature.id} value={feature.id}>
                {feature.name}
              </option>
            ))}
          </select>
        </div>

        {/* Component selector (required) */}
        {selectedFeatureId && (
          <div className="mb-sm">
            <label className="label">Component <span className="text-danger">*</span></label>
            <select
              value={selectedComponentId}
              onChange={(e) => setSelectedComponentId(e.target.value)}
              className="form-control"
              required
            >
              <option value="">Select a component...</option>
              {allComponents
                .filter(c => c.featureId === selectedFeatureId)
                .map(component => (
                  <option key={component.id} value={component.id}>
                    {component.name}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Comment Type */}
        <div className="mb-sm">
          <label className="label">Comment Type <span className="text-danger">*</span></label>
          <select
            value={commentType}
            onChange={(e) => setCommentType(e.target.value as PRFeedbackCommentType)}
            className="form-control"
            required
          >
            <option value="">Select a comment type...</option>
            {commentTypes.map((ct) => (
              <option key={ct.id} value={ct.name}>
                {ct.name}
              </option>
            ))}
          </select>
        </div>

        {/* Severity */}
        <div className="mb-sm">
          <label className="label">Severity <span className="text-danger">*</span></label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as PRFeedbackSeverity)}
            className="form-control"
            required
          >
            <option value="">Select a severity...</option>
            {severities.map((sev) => (
              <option key={sev.id} value={sev.name}>
                {sev.name}
              </option>
            ))}
          </select>
        </div>

        {/* Comment URL */}
        <div className="mb-sm">
          <label className="label">Comment URL <span className="text-danger">*</span></label>
          <input
            type="url"
            placeholder="https://github.com/org/repo/pull/123#issuecomment-..."
            value={commentUrl}
            onChange={(e) => setCommentUrl(e.target.value)}
            className="form-control"
            onKeyPress={(e) => e.key === 'Enter' && handleAddFeedback()}
            required
          />
        </div>

        {/* Description */}
        <div className="mb-md">
          <label className="label">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-control"
            rows={5}
            placeholder="Optional: Describe the feedback, issue, or comment..."
            style={{ resize: 'vertical' }}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddFeedback}
            type="button"
            disabled={
              !prUrl.trim() || 
              !selectedFeatureId || 
              !selectedComponentId
            }
          >
            {editingFeedback ? 'Update Feedback' : 'Add Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PRFeedbackModal;
